// @ts-strict-ignore
/** @jsx jsx */
import React from 'react'
import { jsx } from 'theme-ui'
import MultiStepForm from './multi-step-form'
import { trackEvent, gtmTrack } from '@u/tracking'
import connect from '@h/connect-redux'
import { setIsErrored, setIsLoading } from '@r/elasticsearch/elasticsearch-actions'
import { isErrored, isLoading } from '@r/elasticsearch/elasticsearch-selectors'
import uuidv4 from '@u/guid'
import { readCookieRaw } from '@u/cookies'
import { query, brand, consentUrl, pathname } from '@r/app-selectors'
import isLocalUrl from '@H/is-local-url'
import { callNerdalytics } from '@u/segment-tracking'
import determineRedirect from '@u/determine-redirect'
import { FsAPI } from '@t/fs-api'
import { getComponentById } from '@r/component/component-selectors'

const VariantMultiStepForm = (props: any) => {
  const {
    schema: {
      method,
      integrationType,
      formName,
      defaultProduct,
      idType = '',
      steps,
      redirectLinks = [],
      segmentTracking,
    },
    loading,
    setLoading,
    errored,
    setErrored,
    query: { product_id },
    consentUrl,
    brand,
  } = props

  const { __FS_API_URL__ } = global

  const isFormFieldVisible = (field: any, params: any) => {
    if (!field.visibleIf) return true

    return (
      (field.visibleIf.or || []).some((e) => params[e.field] === e.equals) &&
      (field.visibleIf.and || []).every((e) => params[e.field] === e.equals)
    )
  }

  const setQueryParams = (steps: any, params: any, href: URL) => {
    steps?.forEach((step) => {
      step.fields.forEach((field) => {
        if (field.setInQueryParams && isFormFieldVisible(field, params)) {
          href.searchParams.append(field.name, params[field.name].replace(/,/g, ''))
        }
      })
    })
  }
  const [apiError, setApiError] = React.useState<string | undefined>(undefined)
  const sendDataToAPI = async (params: any) => {
    const guid = generateId(idType)

    const convertedAddresses = convertAddressToApiFormat(steps, params)

    const requestBody: FsAPI.FSRequest = {
      productId: product_id,
      defaultProduct,
      integrationType,
      requestId: guid,
      params: { ...params, ...convertedAddresses },
    }
    setLoading(true)
    try {
      const response = await fetch(
        `${__FS_API_URL__}/form?integrationType=${integrationType}&formName=${formName}&brand=${brand}&deviceId=${readCookieRaw(
          'uscd',
        )}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      )
      const result: FsAPI.FSResponse = await response.json()
      const { action, redirectURL } = result

      const redirect = determineRedirect(redirectLinks, params, redirectURL)

      if (response.status === 200) {
        if (action === 'redirect') {
          const href = new URL(redirect)
          href.searchParams.append('request_id', guid)

          if (product_id) {
            href.searchParams.append('product_id', product_id)
          }

          setQueryParams(steps, params, href)
          if (isLocalUrl(href)) {
            window.location.href = `${href.pathname}${href.search}`
          } else {
            window.location.href = redirect.toString()
          }
        }
      } else {
        setLoading(false)
        if (result.error) {
          setApiError(result.error)
        }
      }
    } catch (error) {
      setLoading(false)
      setErrored(true)
    }
  }

  const convertAddressToApiFormat = (steps: any[], params: any) => {
    const addressLookupKeys = steps
      .flatMap((s) => s.fields)
      .filter((f) => f.type === 'address-lookup' || f.type === 'address-input')
      .map((f) => f.name)

    return Object.fromEntries(
      new Map(
        addressLookupKeys.map((k) => {
          const addresses = JSON.parse(params[k])
          let addressArr = []

          if (Array.isArray(addresses)) {
            addressArr = [...addresses]
          } else {
            addressArr = [addresses]
          }

          return [k, addressArr]
        }),
      ),
    )
  }

  const addressLookup = (postcode: string, setAddresses: (data: any) => void) => {
    const apiPath: string = __FS_API_URL__ || 'https://money.co.uk/api'

    fetch(`${apiPath}/address/${postcode}`)
      .then((response) => response.json())
      .then((response: any) => {
        setAddresses(response.data)
      })
  }

  const newProps = {
    ...props,
    ...(method === 'API' && { submit: sendDataToAPI }),
    isErrored: errored,
    isLoading: loading,
    trackEvent,
    trackInteraction: gtmTrack,
    apiPath: consentUrl,
    brand,
    apiError,
    addressLookup,
    ...(segmentTracking && {
      segmentTracking: () => {
        return new Promise((resolve) => {
          callNerdalytics(segmentTracking.action, segmentTracking.properties, resolve, {
            context: { uscd: readCookieRaw('uscd') },
          })
        })
      },
    }),
  }

  return <MultiStepForm {...newProps} />
}

const ConnectMultiStepForm = connect(
  (state: any, props: any) => ({
    loading: isLoading(state),
    errored: isErrored(state),
    query: query(state),
    consentUrl: consentUrl(state),
    brand: brand(state),
    elasticSearchData: getComponentById(state, props.sys.id)?.elasticSearchData,
    url: pathname(state),
  }),
  (dispatch: any) => ({
    setLoading: (value: boolean) => dispatch(setIsLoading(value)),
    setErrored: (value: boolean) => dispatch(setIsErrored(value)),
  }),
)(VariantMultiStepForm)

export default (props: JSX.IntrinsicAttributes) => <ConnectMultiStepForm {...props} />

const generateId = (idType: string) => {
  if (idType === 'numeric') {
    return Date.now() + Math.random().toString().substr(2, 9)
  } else {
    return uuidv4()
  }
}
